import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import {
    AgenticChildAccordionItem,
    AnalysisSummaryContent,
    AgenticExportCallbackResult,
    ChartVisualizationContent,
} from '../AskAsperAgentic.types';
import Menu from '../../../atoms/Icon/Menu';
import DownloadFile from '../../../atoms/Icon/DownloadFile';
import Stand from '../../../atoms/Icon/Stand';
import ThumbsUp from '../../../atoms/Icon/ThumbsUp';
import ThumbsDown from '../../../atoms/Icon/ThumbsDown';
import Chevron from '../../../atoms/Icon/Chevron';
import AskAsperAccordionMenu, {
    AskAsperAccordionMenuItem,
} from '../../../molecules/AskAsperAccordion/AskAsperAccordionMenu';
import DOCX from '../../../atoms/Icon/DOCX';
import Excel from '../../../atoms/Icon/Excel';
import PDF from '../../../atoms/Icon/PDF';
import PPT from '../../../atoms/Icon/PPT';
import KeyFindings from '../../../molecules/KeyFindings/KeyFindings';
import KeyTakeaways from '../../../molecules/KeyTakeaways/KeyTakeaways';
import { agenticMarkdownComponents } from '../../../shared/markdownComponents';
import MethodologyWithExportCapture from '../shared/MethodologyWithExportCapture';
import Tooltip from '../../../atoms/Tooltip/Tooltip';
import EnhancedBarChart from '../../../molecules/EnhancedBarChart/EnhancedBarChart';
import EnhancedHorizontalBarChart from '../../../molecules/EnhancedHorizontalBarChart/EnhancedHorizontalBarChart';
import EnhancedLineChart from '../../../molecules/EnhancedLineChart/EnhancedLineChart';
import EnhancedStackedBarChart from '../../../molecules/EnhancedStackedBarChart/EnhancedStackedBarChart';
import DataTable, { VizDataFormat } from '../../../molecules/DataTable/DataTable';
import {
    DATA_TABLE_MAX_PREVIEW_BODY_ROWS,
    getTablePreviewStats,
} from '../../../molecules/DataTable/tablePreviewStats';
import TableExportPdfMetadata from '../../../molecules/DataTable/TableExportPdfMetadata';
import BrandPriceLadder from '../../../molecules/BrandPriceLadder/BrandPriceLadder';
import WaterfallChart from '../../../molecules/WaterfallChart/WaterfallChart';
import { VizData, ChartInteractionEventCallback } from '../../../molecules/EnhancedBarChart/EnhancedBarChart.types';
import { BrandPriceLadderVizData } from '../../../molecules/BrandPriceLadder/BrandPriceLadder.types';
import { exportContainerToPDF, exportContainerToDOCX } from '../../../../utils/artifactExport';
import { buildAskAsperExportBasename } from '../../../../utils/exportFilename';
import { isVizDataErrorState } from '../../../../utils/transformVizData';
import useNotification from '../../../atoms/Notification/useNotification';
import {
    openAgenticExportProgressNotification,
    awaitMinimumDurationThenCloseAgenticExportProgress,
    awaitProgressNotificationPaint,
} from '../utils/agenticExportProgressNotification';
import {
    extractTableVizFromChartVisualizationContent,
    exportAgenticTableVizToExcel,
} from '../utils/exportAgenticTableVizToExcel';

// Thumbnail dimensions from Figma design (150px × 109px)
const THUMBNAIL_WIDTH = 150;
const THUMBNAIL_HEIGHT = 109;
// Original card width for scale calculation
const ORIGINAL_CARD_WIDTH = 840;
// Scale factor for miniature preview
const THUMBNAIL_SCALE = THUMBNAIL_WIDTH / ORIGINAL_CARD_WIDTH;

/** Export format for chat/artefact export */
export type AgenticExportFormat = 'docx' | 'xlsx' | 'pdf' | 'ppt';

export interface AgenticFocusRightPanelProps {
    /** Array of accordion items to display in carousel */
    items: AgenticChildAccordionItem[];
    /** Currently active item index */
    activeIndex?: number;
    /** Callback when active item changes */
    onActiveIndexChange?: (index: number) => void;
    /** Callback when close button is clicked */
    onClose?: () => void;
    /** Excel/PPT: parent/API export; PDF/Word: handled in-panel. Return a Promise so progress UI can await API work. */
    onExport?: (format: AgenticExportFormat, run_id?: string, session_id?: string) => AgenticExportCallbackResult;
    /** Callback when Create Case is selected from the dots menu */
    onCreateCase?: () => void;
    /** When true (e.g. disableNavbar mode), shows the focus mode toggle (list/presentation) in the top right of the header */
    showFocusModeToggleInPanel?: boolean;
    /** Callback when focus mode changes - used when showFocusModeToggleInPanel is true (switches back to list view) */
    onViewModeChangeInPanel?: (mode: 'list' | 'presentation') => void;
    /** Callback when an artefact's "View Methodology" section is expanded or collapsed.
     *  Used by hosts for analytics (e.g. RIGHT_PANEL_METHODOLOGY_EXPAND_CLICK /
     *  RIGHT_PANEL_METHODOLOGY_COLLAPSE_CLICK). */
    onMethodologyExpandChange?: (expanded: boolean, artefactTitle?: string) => void;
    /** Callback when the per-artefact export trigger button is clicked. Used by hosts for
     *  analytics (e.g. RIGHT_PANEL_ARTEFACT_EXPORT_CLICK). */
    onArtefactMenuTriggerClick?: (artefactTitle: string) => void;
    /** Callback when a specific export option (Word / PDF / PPT / Excel variants) is clicked for the
     *  active artefact. Used by hosts for analytics (e.g. RIGHT_PANEL_ARTEFACT_EXPORT_OPTION_CLICK).
     *  `exportOption` is a stable identifier such as `docx` | `pdf` | `ppt` | `xlsx_complete_dataset`
     *  | `xlsx_current_view`. */
    onArtefactExportOptionClick?: (exportOption: string, artefactTitle: string) => void;
    /** Generic chart-interaction callback forwarded to all chart visualizations. Used by hosts for
     *  analytics (e.g. RIGHT_PANEL_CHART_*). */
    onTrackChartEvent?: ChartInteractionEventCallback;
    'data-test'?: string;
    'data-test-id'?: string;
}

/**
 * Renders the content for an Analysis Summary accordion
 */
const renderAnalysisSummaryContent = (content: AnalysisSummaryContent, dataTestId?: string) => {
    const hasKeyTakeaways = content.keyTakeaways && content.keyTakeaways.items.length > 0;
    if (!hasKeyTakeaways) return null;

    return (
        <div className="flex flex-col gap-4 px-4">
            {/* Key Takeaways - data from key_takeaways, "Key Findings" is the section title */}
            {hasKeyTakeaways && (
                <KeyFindings
                    title={content.keyTakeaways!.title}
                    items={content.keyTakeaways!.items}
                    renderAsMarkdown
                    data-test-id={`${dataTestId}-key-takeaways`}
                />
            )}
        </div>
    );
};

/** Renders a single chart/table from vizData + chartType */
const renderSingleViz = (
    vizData: any,
    chartType: string,
    vizIndex: number,
    dataTestId?: string,
    onTrackChartEvent?: ChartInteractionEventCallback,
): React.ReactNode => {
    if (isVizDataErrorState(vizData)) return null;
    const normalizedChartType = String(chartType || vizData?.chart_type || 'line').toLowerCase();

    if (normalizedChartType === 'waterfall') {
        return (
            <WaterfallChart
                config={{ viz_data: vizData }}
                showConnectors={false}
                data-test-id={`${dataTestId}-waterfall-chart-${vizIndex}`}
            />
        );
    }
    if (normalizedChartType === 'stackbar') {
        return (
            <EnhancedStackedBarChart
                vizData={vizData as any}
                onBarClick={() => {}}
                onTrackChartEvent={onTrackChartEvent}
                data-test-id={`${dataTestId}-stackbar-chart-${vizIndex}`}
            />
        );
    }
    if (normalizedChartType === 'horizontalbar') {
        return (
            <EnhancedHorizontalBarChart
                vizData={vizData as VizData}
                onBarClick={() => {}}
                onTrackChartEvent={onTrackChartEvent}
                data-test-id={`${dataTestId}-horizontal-bar-chart-${vizIndex}`}
            />
        );
    }
    if (normalizedChartType === 'bar') {
        return (
            <EnhancedBarChart
                vizData={vizData as VizData}
                onBarClick={() => {}}
                onTrackChartEvent={onTrackChartEvent}
                data-test-id={`${dataTestId}-bar-chart-${vizIndex}`}
            />
        );
    }
    if (normalizedChartType === 'line') {
        return (
            <EnhancedLineChart
                vizData={vizData as VizData}
                onPointClick={() => {}}
                onTrackChartEvent={onTrackChartEvent}
                data-test-id={`${dataTestId}-line-chart-${vizIndex}`}
            />
        );
    }
    if (normalizedChartType === 'table') {
        const previewStats = getTablePreviewStats(
            vizData as VizDataFormat,
            DATA_TABLE_MAX_PREVIEW_BODY_ROWS,
        );
        return (
            <>
                <DataTable
                    vizData={vizData as VizDataFormat}
                    maxWidth="100%"
                    maxScrollBodyRows={DATA_TABLE_MAX_PREVIEW_BODY_ROWS}
                    data-test-id={`${dataTestId}-data-table-${vizIndex}`}
                />
                {previewStats && (
                    <TableExportPdfMetadata
                        previewStats={previewStats}
                        data-test-id={`${dataTestId}-table-export-pdf-meta-${vizIndex}`}
                    />
                )}
            </>
        );
    }
    if (normalizedChartType === 'bpl') {
        return (
            <BrandPriceLadder
                vizData={vizData as BrandPriceLadderVizData}
                data-test-id={`${dataTestId}-bpl-chart-${vizIndex}`}
            />
        );
    }
    return null;
};

/**
 * Renders the content for a Chart Visualization
 * Supports multiple visualizations per task (chart + table) in the same card
 */
const renderChartVisualizationContent = (
    content: ChartVisualizationContent,
    dataTestId?: string,
    onMethodologyExpandChange?: (expanded: boolean, artefactTitle?: string) => void,
    artefactTitle?: string,
    onTrackChartEvent?: ChartInteractionEventCallback,
) => {
    const renderChart = () => {
        if (content.customContent) {
            return content.customContent;
        }

        if (content.visualizations && content.visualizations.length > 0) {
            return (
                <div className="flex flex-col gap-6">
                    {content.visualizations.map((item, idx) => (
                        <div key={`viz-${idx}`} className="w-full flex flex-col gap-2">
                            {renderSingleViz(item.vizData, item.chartType ?? '', idx, dataTestId, onTrackChartEvent)}
                            {item.scenarioLink && (
                                <a
                                    href={item.scenarioLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-['Open_Sans'] text-sm text-[#5f49bc] hover:underline"
                                    data-test-id={`${dataTestId}-scenario-link-${idx}`}
                                >
                                    View scenario
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            );
        }

        if (content.vizData) {
            const chartType = content.chartType || content.vizData.chart_type?.toLowerCase() || 'line';
            return renderSingleViz(content.vizData, chartType, 0, dataTestId, onTrackChartEvent);
        }

        return null;
    };

    const hasViz = content.customContent || content.vizData || (content.visualizations?.length ?? 0) > 0;

    return (
        <div className="flex flex-col gap-4 px-4">
            {/* Key Takeaways */}
            {content.keyTakeaways && content.keyTakeaways.items.length > 0 && (
                <KeyTakeaways
                    title={content.keyTakeaways.title}
                    items={content.keyTakeaways.items}
                    renderAsMarkdown
                    customMarkdownComponents={agenticMarkdownComponents}
                    data-test-id={`${dataTestId}-key-takeaways`}
                />
            )}

            {/* Chart/Table visualizations (max 2 per task: chart + table) */}
            {hasViz && <div className="w-full">{renderChart()}</div>}

            {/* Legacy: single scenario link when content.scenarioLink (no per-viz links) */}
            {!content.visualizations?.length && content.scenarioLink && (
                <a
                    href={content.scenarioLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-['Open_Sans'] text-sm text-[#5f49bc] hover:underline"
                    data-test-id={`${dataTestId}-scenario-link`}
                >
                    View scenario
                </a>
            )}

            {/* Methodology (assumptions as bullets; formulae in shaded box when present) */}
            {content.methodology && (content.methodology.sections.length > 0 || content.methodology.formula) && (
                <MethodologyWithExportCapture
                    title={content.methodology.title}
                    sections={content.methodology.sections}
                    formula={content.methodology.formula}
                    defaultExpanded={false}
                    onExpandChange={
                        onMethodologyExpandChange
                            ? (expanded) =>
                                  onMethodologyExpandChange(expanded, artefactTitle ?? content.methodology?.title)
                            : undefined
                    }
                    data-test-id={`${dataTestId}-methodology`}
                />
            )}
        </div>
    );
};

/**
 * Renders accordion content based on type
 */
const renderAccordionContent = (
    content: AnalysisSummaryContent | ChartVisualizationContent | undefined,
    dataTestId?: string,
    onMethodologyExpandChange?: (expanded: boolean, artefactTitle?: string) => void,
    artefactTitle?: string,
    onTrackChartEvent?: ChartInteractionEventCallback,
) => {
    if (!content) return null;

    if (content.type === 'analysisSummary') {
        return renderAnalysisSummaryContent(content, dataTestId);
    }

    if (content.type === 'chartVisualization') {
        return renderChartVisualizationContent(
            content,
            dataTestId,
            onMethodologyExpandChange,
            artefactTitle,
            onTrackChartEvent,
        );
    }

    return null;
};

/**
 * AgenticFocusRightPanel - Carousel view for accordion items
 *
 * Displays accordion items one at a time with:
 * - Vertical Artefacts strip on the left (thumbnail previews)
 * - Previous/Next navigation
 * - Full content display for the active item on the right
 */
const AgenticFocusRightPanel: React.FC<AgenticFocusRightPanelProps> = ({
    items = [],
    activeIndex: controlledActiveIndex,
    onActiveIndexChange,
    onClose,
    onExport,
    showFocusModeToggleInPanel = false,
    onViewModeChangeInPanel,
    onMethodologyExpandChange,
    onArtefactMenuTriggerClick,
    onArtefactExportOptionClick,
    onTrackChartEvent,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();
    const { open: openNotification, success: showSuccessNotification, error: showErrorNotification } =
        useNotification();
    const thumbnailContainerRef = useRef<HTMLDivElement>(null);
    const contentExportRef = useRef<HTMLDivElement>(null);

    // Internal state for active index
    const [internalActiveIndex, setInternalActiveIndex] = useState(0);
    const isControlled = controlledActiveIndex !== undefined;
    const activeIndex = isControlled ? controlledActiveIndex : internalActiveIndex;

    // Content expanded state
    const [isContentExpanded, setIsContentExpanded] = useState(true);

    // Get active item
    const activeItem = items[activeIndex];

    const tableVizForCurrentViewExport = useMemo(() => {
        if (activeItem?.content?.type !== 'chartVisualization') {
            return null;
        }
        return extractTableVizFromChartVisualizationContent(activeItem.content);
    }, [activeItem]);

    // PDF and Word: frontend export via refs. Excel and PPT: delegate to parent (API).
    const handleExport = useCallback(
        async (format: AgenticExportFormat) => {
            openAgenticExportProgressNotification(openNotification, format, {
                'data-test-id': `${dataTestId}-export-progress`,
            });
            const exportProgressOpenedAtMs = performance.now();
            try {
                const singleArtefactTitle = activeItem?.title ?? 'Artefact';
                if (format === 'pdf') {
                    await exportContainerToPDF(contentExportRef.current, {
                        title: singleArtefactTitle,
                        filename: `${buildAskAsperExportBasename(singleArtefactTitle)}.pdf`,
                    });
                    showSuccessNotification({
                        message: 'Export successful',
                        description: `Artifact exported as PDF successfully.`,
                    });
                } else if (format === 'docx') {
                    await exportContainerToDOCX(contentExportRef.current, {
                        title: singleArtefactTitle,
                        filename: `${buildAskAsperExportBasename(singleArtefactTitle)}.docx`,
                    });
                    showSuccessNotification({
                        message: 'Export successful',
                        description: `Artifact exported as Word successfully.`,
                    });
                } else if (onExport) {
                    // Excel complete dataset / PPT: parent API — yield a frame so the LTR progress bar paints, then await the callback (must return a Promise when async).
                    await awaitProgressNotificationPaint();
                    await Promise.resolve(onExport(format, activeItem?.run_id, activeItem?.session_id));
                    showSuccessNotification({
                        message: 'Export successful',
                        description:
                            format === 'xlsx'
                                ? 'Complete dataset exported as Excel successfully.'
                                : `Artifact exported as ${format.toUpperCase()} successfully.`,
                    });
                }
            } catch (err) {
                showErrorNotification({
                    message: 'Export failed',
                    description: err instanceof Error ? err.message : 'Failed to export artifact.',
                });
            } finally {
                await awaitMinimumDurationThenCloseAgenticExportProgress(exportProgressOpenedAtMs);
            }
        },
        [
            onExport,
            activeItem?.title,
            activeItem?.run_id,
            activeItem?.session_id,
            showSuccessNotification,
            showErrorNotification,
            openNotification,
            dataTestId,
        ],
    );

    // Export menu items - flat list so options appear directly (no intermediate "Export" popover)
    const exportMenuItems: AskAsperAccordionMenuItem[] = useMemo(() => {
        const items: AskAsperAccordionMenuItem[] = [
            {
                key: 'export-word',
                label: 'Export as Word',
                icon: <DOCX size={16} />,
                onClick: () => {
                    onArtefactExportOptionClick?.('docx', activeItem?.title ?? '');
                    handleExport('docx');
                },
            },
            {
                key: 'export-excel',
                label: 'Export as Excel',
                icon: <Excel size={16} />,
                submenuPlacement: 'left',
                children: [
                    {
                        key: 'export-excel-complete',
                        label: 'Export complete dataset',
                        onClick: () => {
                            onArtefactExportOptionClick?.('xlsx_complete_dataset', activeItem?.title ?? '');
                            void handleExport('xlsx');
                        },
                    },
                    {
                        key: 'export-excel-current',
                        label: 'Export current view',
                        disabled: !tableVizForCurrentViewExport,
                        onClick: () => {
                            if (!tableVizForCurrentViewExport) {
                                return;
                            }
                            onArtefactExportOptionClick?.('xlsx_current_view', activeItem?.title ?? '');
                            void (async () => {
                                openAgenticExportProgressNotification(openNotification, 'xlsx', {
                                    'data-test-id': `${dataTestId}-export-progress`,
                                });
                                const exportProgressOpenedAtMs = performance.now();
                                try {
                                    await awaitProgressNotificationPaint();
                                    exportAgenticTableVizToExcel(
                                        tableVizForCurrentViewExport,
                                        activeItem?.title,
                                    );
                                    showSuccessNotification({
                                        message: 'Export successful',
                                        description:
                                            'Current table view exported as Excel (data shown in this artefact).',
                                    });
                                } catch (err) {
                                    showErrorNotification({
                                        message: 'Export failed',
                                        description:
                                            err instanceof Error
                                                ? err.message
                                                : 'Could not export the current table view.',
                                    });
                                } finally {
                                    await awaitMinimumDurationThenCloseAgenticExportProgress(
                                        exportProgressOpenedAtMs,
                                    );
                                }
                            })();
                        },
                    },
                ],
            },
            {
                key: 'export-pdf',
                label: 'Export as PDF',
                icon: <PDF size={16} />,
                onClick: () => {
                    onArtefactExportOptionClick?.('pdf', activeItem?.title ?? '');
                    handleExport('pdf');
                },
            },
            {
                key: 'export-ppt',
                label: 'Export as PPT',
                icon: <PPT size={16} />,
                onClick: () => {
                    onArtefactExportOptionClick?.('ppt', activeItem?.title ?? '');
                    handleExport('ppt');
                },
            },
        ];
        if (activeItem?.content.type === 'analysisSummary') {
            return items.filter((m) => m.key !== 'export-excel');
        }
        return items;
    }, [
        handleExport,
        onArtefactExportOptionClick,
        activeItem?.content.type,
        activeItem?.title,
        tableVizForCurrentViewExport,
        showSuccessNotification,
        showErrorNotification,
        openNotification,
        dataTestId,
    ]);

    // Handle index change
    const handleIndexChange = (newIndex: number) => {
        if (newIndex >= 0 && newIndex < items.length) {
            if (!isControlled) {
                setInternalActiveIndex(newIndex);
            }
            onActiveIndexChange?.(newIndex);
        }
    };

    // Navigate to previous item
    const handlePrevious = () => {
        handleIndexChange(activeIndex - 1);
    };

    // Navigate to next item
    const handleNext = () => {
        handleIndexChange(activeIndex + 1);
    };

    // Scroll thumbnail into view when active index changes (vertical strip on left)
    useEffect(() => {
        if (thumbnailContainerRef.current) {
            const thumbnails = thumbnailContainerRef.current.children;
            if (thumbnails[activeIndex]) {
                (thumbnails[activeIndex] as HTMLElement).scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        }
    }, [activeIndex]);

    if (items.length === 0) {
        return (
            <ConfigProvider theme={theme}>
                <div
                    className="flex flex-col h-full min-h-0 bg-[#FAFAFA] overflow-hidden rounded-lg border border-[rgba(0,0,0,0.06)] shadow-[0px_4px_12px_rgba(124,58,237,0.15)]"
                    data-test={dataTest}
                    data-test-id={dataTestId}
                >
                    <div className="flex items-center justify-center h-full">
                        <p className="font-['Open_Sans'] text-[14px] text-[rgba(0,0,0,0.45)]">No artifacts available</p>
                    </div>
                </div>
            </ConfigProvider>
        );
    }

    return (
        <ConfigProvider theme={theme}>
            <div
                className="flex flex-row h-full min-h-0 bg-[#FAFAFA] overflow-hidden rounded-lg border border-[rgba(0,0,0,0.06)] shadow-[0px_4px_12px_rgba(124,58,237,0.15)]"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-agentic-focus-right-panel
            >
                {/* Left: Artefacts Scrolling Strip */}
                <div className="flex flex-col flex-shrink-0 w-[180px] min-h-0 border-r border-[rgba(0,0,0,0.06)] bg-white">
                    <div className="px-4 py-4 shrink-0">
                        <h2 className="font-sans font-semibold text-[16px] leading-normal text-[#18181b]">Artefacts</h2>
                    </div>
                    <div className="h-px bg-[rgba(0,0,0,0.06)] w-full shrink-0" />
                    <div
                        ref={thumbnailContainerRef}
                        className="flex-1 flex flex-col gap-3 overflow-y-auto px-4 py-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
                        data-test-id={`${dataTestId}-thumbnails`}
                    >
                        {items.map((item, index) => (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => handleIndexChange(index)}
                                className={`flex-shrink-0 rounded-lg border-2 transition-all overflow-hidden cursor-pointer bg-white ${
                                    index === activeIndex
                                        ? 'border-[#7C3AED]'
                                        : 'border-[rgba(0,0,0,0.06)] hover:border-[rgba(0,0,0,0.12)]'
                                }`}
                                style={{
                                    width: `${THUMBNAIL_WIDTH}px`,
                                    height: `${THUMBNAIL_HEIGHT}px`,
                                }}
                                data-test-id={`${dataTestId}-thumbnail-${item.id}`}
                            >
                                {/* Miniature React Component Preview */}
                                <div
                                    className="origin-top-left pointer-events-none"
                                    style={{
                                        width: `${ORIGINAL_CARD_WIDTH}px`,
                                        transform: `scale(${THUMBNAIL_SCALE})`,
                                    }}
                                >
                                    <div className="bg-white rounded-lg border border-[#e4e4e7] overflow-hidden">
                                        {/* Mini Card Header */}
                                        <div className="px-5 py-4">
                                            <div className="flex items-start justify-between">
                                                <div className="flex flex-col gap-1">
                                                    <span
                                                        className="font-['Open_Sans'] text-[16px] leading-normal text-[#140b1d]"
                                                        style={{ fontWeight: 600 }}
                                                    >
                                                        {item.title}
                                                    </span>
                                                    {item.subtitle && (
                                                        <span className="font-['Open_Sans'] text-[14px] leading-normal text-[rgba(0,0,0,0.45)]">
                                                            {item.subtitle}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="h-px bg-[#e4e4e7] w-full" />
                                        {/* Mini Content */}
                                        <div className="px-5 py-4">
                                            {renderAccordionContent(item.content, `${dataTestId}-thumb-${item.id}`)}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Right: Main Content */}
                <div className="flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden">
                    <div className="bg-white sticky top-0 z-10 shrink-0">
                        <div className="px-4 md:px-6 py-3">
                            <div className="flex items-center justify-end gap-2">
                                {/* Focus Mode Toggle - shown when navbar is disabled (switches back to list view) */}
                                {showFocusModeToggleInPanel && onViewModeChangeInPanel && (
                                    <div className="bg-[#f4f4f5] flex items-center p-0.5 rounded-md mr-2">
                                        <Tooltip
                                            title="Return to list view"
                                            placement="bottom"
                                            getPopupContainer={() => document.body}
                                        >
                                            <button
                                                type="button"
                                                className="flex items-center justify-center w-7 h-7 rounded cursor-pointer transition-all"
                                                onClick={() => onViewModeChangeInPanel('list')}
                                                aria-label="Return to list view"
                                                data-test-id={`${dataTestId}-focus-mode-list`}
                                            >
                                                <Menu size={20} color="rgba(0,0,0,0.45)" />
                                            </button>
                                        </Tooltip>
                                        <Tooltip
                                            title="Presentation view"
                                            placement="bottom"
                                            getPopupContainer={() => document.body}
                                        >
                                            <button
                                                type="button"
                                                className="flex items-center justify-center w-7 h-7 rounded cursor-pointer transition-all bg-white shadow-sm"
                                                onClick={() => onViewModeChangeInPanel('presentation')}
                                                aria-label="Presentation view"
                                                data-test-id={`${dataTestId}-focus-mode-presentation`}
                                            >
                                                <Stand size={20} color="rgba(0,0,0,0.88)" />
                                            </button>
                                        </Tooltip>
                                    </div>
                                )}
                                {/* Navigation - moved to header area */}
                                <button
                                    type="button"
                                    onClick={handlePrevious}
                                    disabled={activeIndex === 0}
                                    className={`flex items-center justify-center size-8 rounded border border-[#e4e4e7] bg-white transition-colors ${
                                        activeIndex === 0
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'cursor-pointer hover:bg-gray-50'
                                    }`}
                                    aria-label="Previous artefact"
                                    data-test-id={`${dataTestId}-prev`}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M15 18L9 12L15 6"
                                            stroke="#71717A"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                                <span className="font-['Open_Sans'] text-[14px] text-[rgba(0,0,0,0.65)]">Artefact</span>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    disabled={activeIndex === items.length - 1}
                                    className={`flex items-center justify-center size-8 rounded border border-[#e4e4e7] bg-white transition-colors ${
                                        activeIndex === items.length - 1
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'cursor-pointer hover:bg-gray-50'
                                    }`}
                                    aria-label="Next artefact"
                                    data-test-id={`${dataTestId}-next`}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M9 6L15 12L9 18"
                                            stroke="#71717A"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="h-px bg-[rgba(0,0,0,0.06)] w-full" />
                    </div>

                    {/* Main Content - scroll inside outer container, summary block fills available height */}
                    <div className="flex-1 min-h-0 flex flex-col overflow-y-auto px-4 md:px-6 py-4 md:py-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                        {activeItem && (
                            <div
                                ref={contentExportRef}
                                className="flex-1 min-h-full flex flex-col bg-white rounded-[12px] border border-[#d0d5f5] overflow-hidden shadow-[0px_11px_15px_0px_rgba(99,140,243,0.25)]"
                            >
                                {/* Card Header */}
                                <div className="shrink-0 px-5 py-4">
                                    <div className="flex items-start justify-between">
                                        {/* Title and Subtitle */}
                                        <div className="flex flex-col gap-1">
                                            <span
                                                className="font-['Open_Sans'] text-[16px] leading-normal text-[#140b1d]"
                                                style={{ fontWeight: 600 }}
                                            >
                                                {activeItem.title}
                                            </span>
                                            {activeItem.subtitle && (
                                                <span className="font-['Open_Sans'] text-[14px] leading-normal text-[rgba(0,0,0,0.45)]">
                                                    {activeItem.subtitle}
                                                </span>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        <div className="flex gap-1 items-center">
                                            {/* Feedback group */}
                                            <div className="border border-[#e4e4e7] border-solid rounded flex items-center overflow-hidden">
                                                <button
                                                    type="button"
                                                    className="bg-white flex items-center justify-center p-0 size-6 cursor-pointer hover:bg-gray-50 border-none"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        activeItem.onThumbsDown?.();
                                                    }}
                                                    aria-label="Thumbs down"
                                                >
                                                    <ThumbsDown
                                                        size={16}
                                                        strokeColor="#71717A"
                                                        color="transparent"
                                                        strokeWidth={1.5}
                                                    />
                                                </button>
                                                <div className="w-px h-6 bg-[#e4e4e7]" />
                                                <button
                                                    type="button"
                                                    className="bg-white flex items-center justify-center p-0 size-6 cursor-pointer hover:bg-gray-50 border-none"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        activeItem.onThumbsUp?.();
                                                    }}
                                                    aria-label="Thumbs up"
                                                >
                                                    <ThumbsUp
                                                        size={16}
                                                        strokeColor="#71717A"
                                                        color="transparent"
                                                        strokeWidth={1.5}
                                                    />
                                                </button>
                                            </div>

                                            {/* Export button - next to feedback buttons */}
                                            <AskAsperAccordionMenu
                                                items={exportMenuItems}
                                                placement="bottomRight"
                                                data-test-id={`${dataTestId}-export-menu`}
                                            >
                                                <button
                                                    type="button"
                                                    className="bg-white border border-[#e4e4e7] border-solid flex items-center justify-center rounded p-0 size-6 cursor-pointer hover:bg-gray-50"
                                                    aria-label="Export"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (onArtefactMenuTriggerClick && activeItem) {
                                                            onArtefactMenuTriggerClick(activeItem.title);
                                                        }
                                                    }}
                                                    data-test-id={`${dataTestId}-export`}
                                                >
                                                    <DownloadFile size={12} color="#71717A" />
                                                </button>
                                            </AskAsperAccordionMenu>

                                            {/* Collapse toggle */}
                                            <button
                                                type="button"
                                                className="bg-transparent border-none p-0 cursor-pointer flex items-center justify-center size-6"
                                                onClick={() => setIsContentExpanded(!isContentExpanded)}
                                                aria-expanded={isContentExpanded}
                                                aria-label={isContentExpanded ? 'Collapse content' : 'Expand content'}
                                            >
                                                <Chevron
                                                    size={10}
                                                    color="#71717a"
                                                    rotate={isContentExpanded ? 0 : 180}
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Divider */}
                                {isContentExpanded && <div className="h-px bg-[#e4e4e7] w-full shrink-0" />}

                                {/* Content - fills remaining card height, scrolls when content exceeds */}
                                {isContentExpanded && (
                                    <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                                        {renderAccordionContent(
                                            activeItem.content,
                                            `${dataTestId}-content-${activeItem.id}`,
                                            onMethodologyExpandChange,
                                            activeItem.title,
                                            onTrackChartEvent,
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default AgenticFocusRightPanel;
